import { useMemo } from 'react';
import { Alert, Button, Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReportDefinition, ReportFilterOption, ReportRunResult } from '@goelectric/shared-types';
import { columnAlignment, formatCell } from '../utils/format';

type ReportRow = Record<string, unknown>;

export interface ReportPreviewTableProps {
  definition: ReportDefinition;
  /** Claves a mostrar y su orden; si se omite, van todas las declaradas. */
  visibleColumns?: string[];
  /** Opciones de catálogos por `origen`, indexadas por la clave de la columna. */
  catalogoOpciones?: Record<string, ReportFilterOption[]>;
  result: ReportRunResult | null;
  loading: boolean;
  error: string | null;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}

/**
 * Vista previa generada desde `ReportDefinition.columnas`: encabezado, tipo y
 * alineación salen de la definición, no de código por reporte. Paginación
 * server-side sobre `meta.total`.
 */
export function ReportPreviewTable({
  definition,
  visibleColumns,
  catalogoOpciones,
  result,
  loading,
  error,
  page,
  limit,
  onPageChange,
  onRetry,
}: ReportPreviewTableProps) {
  const columns = useMemo<ColumnsType<ReportRow>>(() => {
    const declaradas = new Map(
      definition.columnas.map((columna) => [columna.clave, columna] as const),
    );
    const ordenadas = visibleColumns
      ? visibleColumns.flatMap((clave) => {
          const columna = declaradas.get(clave);
          return columna ? [columna] : [];
        })
      : definition.columnas;

    return ordenadas.map((columna) => ({
      key: columna.clave,
      dataIndex: columna.clave,
      title: columna.titulo,
      align: columnAlignment(columna.tipo),
      ellipsis: true,
      render: (value: unknown) =>
        formatCell(value, columna.tipo, catalogoOpciones?.[columna.clave] ?? columna.opciones),
    }));
  }, [definition.columnas, visibleColumns, catalogoOpciones]);

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudo ejecutar el reporte"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <>
      {result?.meta.truncado && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Resultado truncado"
          description="El motor alcanzó el tope de filas: esta vista y el archivo descargado vienen incompletos. Acotá los filtros para obtener el resultado completo."
        />
      )}

      <Table<ReportRow>
        rowKey={(row) => definition.columnas.map((columna) => String(row[columna.clave] ?? '')).join('|')}
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={result?.data ?? []}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize: limit,
          total: result?.meta.total ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `${total} filas`,
          onChange: onPageChange,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin filas para los filtros aplicados"
            />
          ),
        }}
      />
    </>
  );
}
